/**
 * CoffeeMates Loyalty Service — stamp cards, rewards, wallet passes
 */

import httpClient from '../utils/httpClient';

export interface LoyaltyCard {
  card_id: string;
  venue_id: string;
  venue_name: string;
  venue_photo: string | null;
  venue_slug: string | null;
  stamps_count: number;
  stamps_required: number;
  total_stamps: number;
  rewards_available: number;
  rewards_earned: number;
  rewards_redeemed: number;
  last_stamp_at: string | null;
  apple_pass_serial: string | null;
  google_pass_object_id: string | null;
}

export interface StampResult {
  success: boolean;
  venue_name: string;
  venue_photo: string | null;
  stamps_count: number;
  stamps_required: number;
  total_stamps: number;
  is_new_card: boolean;
  reward_earned: boolean;
  reward: {
    reward_id: string;
    description: string;
    qr_token: string;
    expires_at: string;
  } | null;
  card_id: string;
  message: string;
}

export interface CardDetail {
  card_id: string;
  venue: {
    id: string;
    name: string;
    photo: string | null;
    slug: string | null;
    address: string | null;
    latitude: number | null;
    longitude: number | null;
  };
  program: {
    name: string;
    stamps_required: number;
    reward_description: string;
    reward_type: string;
  };
  stamps_count: number;
  stamps_required: number;
  total_stamps: number;
  rewards_earned: number;
  rewards_redeemed: number;
  stamp_history: Array<{
    stamp_number: number;
    source: string;
    is_reward_stamp: boolean;
    created_at: string;
  }>;
  rewards: Array<{
    id: string;
    reward_number: number;
    description: string;
    qr_token: string | null;
    status: string;
    expires_at: string | null;
    redeemed_at: string | null;
  }>;
  apple_pass_serial: string | null;
  google_pass_object_id: string | null;
  created_at: string;
}

export interface StampInfo {
  venue_name: string;
  venue_photo: string | null;
  stamps_required: number;
  reward_description: string;
  program_active: boolean;
}

const loyaltyService = {
  /** Record a stamp (after scanning venue QR) */
  async stamp(venueToken: string): Promise<StampResult> {
    const { data } = await httpClient.post<StampResult>(`/loyalty/stamp?venue_token=${encodeURIComponent(venueToken)}`);
    return data;
  },

  /** Get stamp info for landing page (public, no auth) */
  async getStampInfo(venueToken: string): Promise<StampInfo> {
    const { data } = await httpClient.get<StampInfo>(`/loyalty/stamp-info/${venueToken}`);
    return data;
  },

  /** List user's loyalty cards */
  async getMyCards(): Promise<LoyaltyCard[]> {
    const { data } = await httpClient.get<LoyaltyCard[]>('/loyalty/my-cards');
    return data;
  },

  /** Get card detail with stamp history and rewards */
  async getCardDetail(cardId: string): Promise<CardDetail> {
    const { data } = await httpClient.get<CardDetail>(`/loyalty/cards/${cardId}`);
    return data;
  },

  /** Redeem a reward */
  async redeemReward(rewardId: string): Promise<{ success: boolean; message: string }> {
    const { data } = await httpClient.post(`/loyalty/rewards/${rewardId}/redeem`);
    return data;
  },

  /** Get Apple Wallet .pkpass download (returns blob) */
  async getApplePass(cardId: string): Promise<Blob> {
    return httpClient.getBlob(`/loyalty/cards/${cardId}/apple-pass`);
  },

  /** Get Google Wallet save URL */
  async getGoogleWalletUrl(cardId: string): Promise<{ save_url: string }> {
    const { data } = await httpClient.get(`/loyalty/cards/${cardId}/google-wallet`);
    return data;
  },

  /**
   * Staff: manually stamp a customer who can't show their phone QR.
   * Falls back to phone OR email lookup. Same cooldown + reward unlock as the
   * QR-scan path; a `LoyaltyStamp` row is recorded with `source='manual'` and
   * the staff member is attributed via `stamped_by_staff_id`.
   */
  async manualStamp(
    identifier: string,
    identifier_type: 'phone' | 'email',
    reason?: string,
  ): Promise<StampResult> {
    const body: { identifier: string; identifier_type: 'phone' | 'email'; reason?: string } = {
      identifier,
      identifier_type,
    };
    if (reason && reason.trim()) body.reason = reason.trim();
    const { data } = await httpClient.post<StampResult>('/loyalty/stamp/manual', body);
    return data;
  },
};

export default loyaltyService;
